/**
 * Phaser-side palette mirroring the CSS variables in `tokens.css`.
 *
 * Phaser's rendering APIs (Graphics.fillStyle, Rectangle.fillColor, Text.color,
 * Game.config.backgroundColor) cannot read CSS custom properties — they need
 * hex integers or CSS-color strings literal. This module bakes the Future
 * Cartridge tokens into Phaser-consumable formats.
 *
 * SYNC RULE
 * ─────────
 * Any change to `tokens.css` that touches the source rgba() / hex values for
 * any of the constants below MUST update the corresponding constant here.
 * `apps/game/test/phaser-palette-sync.test.ts` parses `tokens.css` at test
 * time and asserts each (hex, alpha) tuple matches — both sides. Drift on
 * either color OR alpha is a test failure.
 *
 * Precedent: `STATUS_TINT_HEX` (PR 3, status sprite tint, opaque-only).
 * Difference: glass tokens carry load-bearing alpha for Phaser fillAlpha /
 * lineStyle alpha. Each glass export is a `{hex, alpha, rgba}` triple — the
 * sync test checks all three views are consistent.
 *
 * USAGE
 * ─────
 *   - `Phaser.GameObjects.Graphics.fillStyle(GLASS_FILL_MID.hex, GLASS_FILL_MID.alpha)`
 *   - `Phaser.GameObjects.Rectangle(..., GLASS_FILL_MID.hex, GLASS_FILL_MID.alpha)`
 *   - `Phaser.GameObjects.Text(..., { color: TEXT_PRIMARY.rgba })` — preserves alpha
 *   - `cameras.main.backgroundColor = CANVAS_BG_HEX` — opaque
 */

/** Color + alpha tuple, with a canonical CSS string for Text.color use. */
export interface GlassToken {
  /** 0xRRGGBB int for Phaser fillColor / lineStyle color arg. */
  readonly hex: number;
  /** 0-1 alpha for Phaser fillAlpha / lineStyle alpha arg. */
  readonly alpha: number;
  /** Canonical CSS rgba()/hex string — use for Phaser.Text.color when alpha matters. */
  readonly rgba: string;
}

const tok = (hex: number, alpha: number, rgba: string): GlassToken =>
  Object.freeze({ hex, alpha, rgba });

// ─── GLASS SURFACES ────────────────────────────────────────────────────
// rgb(20, 22, 28) = 0x14161c — the dark glass base color shared across all
// three opacity tiers. Matches body bg #0E1014 with a slight luminance lift.

export const GLASS_FILL_THIN = tok(0x14161c, 0.4, 'rgba(20, 22, 28, 0.40)');
export const GLASS_FILL_MID = tok(0x14161c, 0.55, 'rgba(20, 22, 28, 0.55)');
export const GLASS_FILL_THICK = tok(0x14161c, 0.72, 'rgba(20, 22, 28, 0.72)');

// Full-canvas wash over a frozen-world snapshot — body-bg hue (14,16,20 =
// 0x0e1014) rather than the glass base, so panels drawn on top of the wash
// still read as lighter sheets. CSS twin: `--scrim-wash` (.draft-modal;
// battle glass stage consumes this Phaser-side).
export const SCRIM_WASH = tok(0x0e1014, 0.45, 'rgba(14, 16, 20, 0.45)');

// ─── SPECULAR ─────────────────────────────────────────────────────────
// rgb(255, 255, 255) at low alpha — the top-edge highlight that sells
// "glass material" on every Future Cartridge panel.

export const SPECULAR_EDGE = tok(0xffffff, 0.12, 'rgba(255, 255, 255, 0.12)');
export const SPECULAR_EDGE_EMPHASIS = tok(
  0xffffff,
  0.2,
  'rgba(255, 255, 255, 0.20)',
);

// ─── TEXT ─────────────────────────────────────────────────────────────
// rgb(255, 255, 255) — three roles by alpha. Primary for body, secondary
// for ghosted labels, tertiary for captions and empty/placeholder states.

export const TEXT_PRIMARY = tok(0xffffff, 0.96, 'rgba(255, 255, 255, 0.96)');
export const TEXT_SECONDARY = tok(0xffffff, 0.68, 'rgba(255, 255, 255, 0.68)');
export const TEXT_TERTIARY = tok(0xffffff, 0.44, 'rgba(255, 255, 255, 0.44)');

// ─── STATE (opaque) ───────────────────────────────────────────────────
// HP threshold + dev-overlay-collision. Opaque hex; alpha is render-side
// concern only (e.g. collision overlay uses 0.35 alpha on top of ERR_RED).

export const ERR_RED = tok(0xff453a, 1.0, '#FF453A');

// ─── CANVAS BG ────────────────────────────────────────────────────────
// Phaser game config backgroundColor. Matches the body bg in index.html
// (#0E1014). Not a CSS var because Phaser snapshots it once at game-init;
// the value is shared with index.html via convention, not import.

export const CANVAS_BG_HEX = 0x0e1014;
